/**
 * Search drill-down routing — the single source of truth for where each result type opens, kept as a
 * pure function so it's trivially testable and identical for the overlay and the /search route.
 *
 *   member  (email match) -> that customer's Users tab        /customers/{id}?tab=users&member={email}
 *   pending (email match, pending invitee) -> the SAME link; the Users tab opens its Pending sub-tab
 *           by itself when ?member= names a pending email (UsersTab.tsx), so no second route exists
 *   account (name/number) -> Customer-360 overview            /customers/{id}
 *   invoice (number match) -> that customer's Invoices tab    /customers/{id}?tab=invoice
 *
 * The Customer-360 tab keys (`users`, `invoice`) match customer.tabs.* in i18n and the tab ids the
 * 360 screen reads from the `?tab=` query param. A member match also carries the matched email in
 * ?member= so the Users tab can highlight + scroll to that exact row. Ids/emails are URL-encoded.
 */

import type { SearchAccount, SearchInvoice, SearchMember, SearchPending } from "@/api/types";

/** An invoice hit that HAS a real account_id — the only shape drillPath ever accepts. An
 * unattributed hit (account_id null, or attribution_state "unattributed"/"master_account") is
 * rendered as a non-clickable badge instead (SearchResults.tsx) and never reaches here — see
 * K2-A: drilling one would build "/customers/null". */
export type AttributedSearchInvoice = SearchInvoice & { account_id: string };

export type DrillTarget =
  | { kind: "member"; item: SearchMember }
  | { kind: "pending"; item: SearchPending }
  | { kind: "account"; item: SearchAccount }
  | { kind: "invoice"; item: AttributedSearchInvoice };

/** The Customer-360 tab a target opens on (overview when omitted). */
export type CustomerTab = "users" | "invoice";

export function drillPath(target: DrillTarget): string {
  const id = encodeURIComponent(target.item.account_id);
  switch (target.kind) {
    case "member":
    case "pending":
      return `/customers/${id}?tab=users&member=${encodeURIComponent(target.item.email)}`;
    case "invoice":
      return `/customers/${id}?tab=invoice`;
    case "account":
      return `/customers/${id}`;
  }
}
